/**
 * Socket Connection Mutex
 *
 * Manages socket connection lifecycle for self-hosted connections.
 * Ensures only one socket instance is active at a time.
 *
 * Part of Phase 1: Event Deduplication & Connection Gating
 */

export type SocketType = 'self-hosted';

interface ConnectionState {
  activeType: SocketType | null;
  activeSocketId: string | null;
  acquiredAt: number | null;
}

// Global connection state
const state: ConnectionState = {
  activeType: null,
  activeSocketId: null,
  acquiredAt: null,
};

// Auto-release timeout (30 seconds) - prevents deadlock on crash
const AUTO_RELEASE_TIMEOUT_MS = 30_000;

// Listeners for connection state changes
type ConnectionListener = (type: SocketType | null, socketId: string | null) => void;
const listeners = new Set<ConnectionListener>();

/**
 * Attempt to acquire the connection mutex for a socket type.
 *
 * @param type - The socket type requesting connection ('cloud' or 'self-hosted')
 * @param socketId - Unique identifier for this socket instance
 * @returns true if connection was acquired, false if blocked
 *
 * @example
 * ```ts
 * const socketId = `cloud-${Date.now()}`;
 * if (acquireConnection('cloud', socketId)) {
 *   // Proceed with connection
 *   socket.connect();
 * } else {
 *   console.warn('Another socket type is already active');
 * }
 * ```
 */
export function acquireConnection(type: SocketType, socketId: string): boolean {
  // Check for stale connection (auto-release after timeout)
  if (state.activeType && state.acquiredAt) {
    const elapsed = Date.now() - state.acquiredAt;
    if (elapsed > AUTO_RELEASE_TIMEOUT_MS) {
      console.warn(
        `[SocketMutex] Auto-releasing stale connection: ${state.activeType} (${state.activeSocketId}) after ${elapsed}ms`
      );
      releaseConnection(state.activeSocketId!);
    }
  }

  // If same type is already active, allow (reconnection scenario)
  if (state.activeType === type) {
    // Update socket ID if different (new socket instance for same type)
    if (state.activeSocketId !== socketId) {
      console.log(
        `[SocketMutex] Updating ${type} socket ID: ${state.activeSocketId} -> ${socketId}`
      );
      state.activeSocketId = socketId;
      state.acquiredAt = Date.now();
    }
    return true;
  }

  // If different type is active, block
  if (state.activeType && state.activeType !== type) {
    console.warn(
      `[SocketMutex] Blocked ${type} connection - ${state.activeType} already active (${state.activeSocketId})`
    );
    return false;
  }

  // Acquire connection
  state.activeType = type;
  state.activeSocketId = socketId;
  state.acquiredAt = Date.now();

  console.log(`[SocketMutex] Acquired: ${type} (${socketId})`);
  notifyListeners();

  return true;
}

/**
 * Release the connection mutex for a socket.
 *
 * @param socketId - The socket ID to release (must match active socket)
 *
 * @example
 * ```ts
 * socket.on('disconnect', () => {
 *   releaseConnection(socketId);
 * });
 * ```
 */
export function releaseConnection(socketId: string): void {
  if (state.activeSocketId !== socketId) {
    console.log(
      `[SocketMutex] Ignoring release for non-active socket: ${socketId} (active: ${state.activeSocketId})`
    );
    return;
  }

  console.log(`[SocketMutex] Released: ${state.activeType} (${socketId})`);

  state.activeType = null;
  state.activeSocketId = null;
  state.acquiredAt = null;

  notifyListeners();
}

/**
 * Force release the connection mutex regardless of socket ID.
 * Use with caution - only for cleanup scenarios.
 */
export function forceRelease(): void {
  if (state.activeType) {
    console.warn(
      `[SocketMutex] Force released: ${state.activeType} (${state.activeSocketId})`
    );
  }

  state.activeType = null;
  state.activeSocketId = null;
  state.acquiredAt = null;

  notifyListeners();
}

/**
 * Refresh the connection timestamp to prevent auto-release.
 * Call this periodically (e.g., on each received message) to keep connection alive.
 *
 * @param socketId - The socket ID to refresh
 */
export function refreshConnection(socketId: string): void {
  if (state.activeSocketId === socketId) {
    state.acquiredAt = Date.now();
  }
}

/**
 * Get current connection state (read-only).
 */
export function getConnectionState(): Readonly<ConnectionState> {
  return { ...state };
}

/**
 * Check if a specific socket type is currently active.
 */
export function isTypeActive(type: SocketType): boolean {
  return state.activeType === type;
}

/**
 * Check if any socket connection is active.
 */
export function isAnyConnectionActive(): boolean {
  return state.activeType !== null;
}

/**
 * Subscribe to connection state changes.
 *
 * @param listener - Callback invoked when connection state changes
 * @returns Unsubscribe function
 */
export function onConnectionChange(listener: ConnectionListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Notify all listeners of connection state change.
 */
function notifyListeners(): void {
  listeners.forEach((listener) => {
    try {
      listener(state.activeType, state.activeSocketId);
    } catch (error) {
      console.error('[SocketMutex] Listener error:', error);
    }
  });
}

/**
 * Generate a unique socket ID for connection tracking.
 *
 * @param type - Socket type prefix
 * @returns Unique socket ID string
 */
export function generateSocketId(type: SocketType): string {
  return `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
